import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useExplore from '../hooks/useExplore';
import { EXPLORE_STEPS, getNextStep, getPrevStep, getStep, getStepIndex } from '../utils/routes';
import './Explore.css';

export default function ExploreLayout({ stepId, eyebrow, title, subtitle, children, nextLabel = 'Continue', hideFooter = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, progress, visitStep, completeStep, skipStep, reset } = useExplore();
  const currentStep = getStep(stepId);
  const currentIndex = getStepIndex(stepId);
  const nextStep = getNextStep(stepId);
  const prevStep = getPrevStep(stepId);

  useEffect(() => {
    visitStep(stepId);
  }, [stepId, visitStep]);

  const handleNext = () => {
    completeStep(stepId);
    if (nextStep?.path && nextStep.id !== stepId) navigate(nextStep.path);
  };

  const handleBack = () => {
    if (prevStep?.path && prevStep.id !== stepId) navigate(prevStep.path);
  };

  const handleSkip = () => {
    skipStep(stepId);
    if (nextStep?.path && nextStep.id !== stepId) navigate(nextStep.path);
  };

  return (
    <main className="explore-shell">
      <div className="explore-bg-grid" />
      <div className="explore-orb explore-orb-one" />
      <div className="explore-orb explore-orb-two" />

      <header className="explore-topbar">
        <Link to="/" className="explore-brand" aria-label="MatrixOS home">
          <span className="explore-brand-mark">M</span>
          <span>
            <strong>MatrixOS</strong>
            <small>Explore</small>
          </span>
        </Link>

        <div className="explore-top-actions">
          <button type="button" className="explore-ghost-btn" onClick={reset}>Reset</button>
          <Link className="explore-ghost-btn" to="/">Exit</Link>
        </div>
      </header>

      <section className="explore-progress-card" aria-label="Explore progress">
        <div className="explore-progress-meta">
          <span>{currentStep.label}</span>
          <strong>{progress}%</strong>
        </div>
        <div className="explore-progress-track">
          <div className="explore-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="explore-step-dots">
          {EXPLORE_STEPS.map((step, index) => {
            const active = step.id === stepId;
            const complete = (state.completedSteps || []).includes(step.id) || index < currentIndex;
            return (
              <Link
                key={step.id}
                to={step.path}
                className={`explore-step-dot ${active ? 'active' : ''} ${complete ? 'complete' : ''}`}
                title={step.label}
              >
                <span>{index + 1}</span>
                <small>{step.label}</small>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="explore-content-card">
        <div className="explore-page-heading">
          {eyebrow && <p className="explore-eyebrow">{eyebrow}</p>}
          <h1>{title}</h1>
          {subtitle && <p className="explore-subtitle">{subtitle}</p>}
        </div>

        <div className="explore-page-body">{children}</div>
      </section>

      {!hideFooter && (
        <footer className="explore-footer-nav">
          <button type="button" className="explore-secondary-btn" onClick={handleBack} disabled={currentIndex <= 0}>
            ← Back
          </button>
          <div className="explore-footer-right">
            <button type="button" className="explore-secondary-btn" onClick={handleSkip}>
              Skip
            </button>
            <button type="button" className="explore-primary-btn" onClick={handleNext}>
              {nextLabel} →
            </button>
          </div>
        </footer>
      )}

      <div className="explore-route-hint">{location.pathname}</div>
    </main>
  );
}
